<template>
    <div class="link-summary">
        <div class="link-summary__container">
            <div class="link-summary__cta" v-if="isMobile">
                <span class="link-summary__total-text" tabindex="0"> {{ $ml.get('totalAmount') }} </span>

                <button @click="shouldShowContent = !shouldShowContent" class="link-summary__toggle-button" :aria-label="isMobile ? $ml.get('ariaShowOrderSummaryContent') : ''" :tabindex="isMobile ? '0' : '-1'">
                    <span :tabindex="isMobile ? '-1' : '0'">
                        {{ collapsedTotalText }}
                    </span>

                    <img :class="{ active: shouldShowContent }" src="@/assets/img/icons/arrow-down-blue.svg" alt="" tabindex="-1" />
                </button>
            </div>
 
            <transition name="fade">
                <div v-if="shouldShowContent" class="link-summary__dropdown">
                    <!-- Section: Nome e logo do merchant -->
                    <div class="link-summary__merchant-info">
                        <hr class="link-summary__divider" v-if="isMobile" />
                        <div class="link-summary__row">
                            <MerchantIdentity
                                v-if="!isCustomizationEnabled"
                                variant="summary"
                                logo-source="legacy"
                                :show-text-fallback="true"
                            />
                            <div class="link-summary__container">
                                <span tabindex="0">{{ merchantName }}</span>
                                <small tabindex="0">{{ $ml.get('linkInfo') }}</small>
                            </div>
                        </div>
                    </div>

                    <!-- Section: Lista de items no carrinho -->
                    <div class="link-summary__cart-items">
                        <hr class="link-summary__divider" />
                        <CartItems :is-desktop="!isMobile" />
                    </div>

                    <!-- Section: Descrição do primeiro item -->
                    <div class="link-summary__item-description" v-if="firstItemDescription">
                        <hr class="link-summary__divider" />
                        <CartItemDescription
                            :is-short-description="true"
                            :item-description="firstItemDescription"
                        />
                    </div>

                    <!-- Section: Entrega -->
                    <div v-if="shippingMethod && showShippingMethod" class="link-summary__shipping-info">
                        <hr class="link-summary__divider" />
                        <strong class="link-summary__title" v-text="$ml.get('delivery')" tabindex="0" />
                        <ShippingMethodOverview :minimal="false" />
                    </div>

                    <!-- Section: Pagamento -->
                    <div v-if="showPaymentInfo" class="link-summary__payment-info">
                        <hr class="link-summary__divider" />
                        <strong class="link-summary__title" v-text="$ml.get('payment')" tabindex="0" />
                        <CreditCardOverview />
                        <BoletoOverview />
                        <PixOverview />
                        <PixOpenFinanceOverview />
                        <QrCodePayOverview />
                        <ClickToPayOverview />
                        <GooglePayOverview />
                        <ApplePayOverview />
                    </div>

                    <!-- Section: Data de pagamento -->
                    <div class="link-summary__payment-date" v-if="showPaymentDate">
                        <hr class="link-summary__divider" />
                        <strong class="link-summary__title" v-text="$ml.get('paymentDate')" tabindex="0" />
                        <PaymentDateOverview />
                    </div>

                    <!-- Section: totalizador -->
                    <div class="link-summary__total">
                        <hr class="link-summary__divider" />
                        <TotalizerOverview
                            :is-mobile="isMobile"
                            :has-convenience-fee="hasConvenienceFee"
                            :formatted-price="formattedPrice"
                            :formatted-subtotal-price="formattedSubtotalPrice"
                        />
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
    import moneyHelper from '@/helpers/moneyHelper';
    import CreditCardOverview from '@/components/OrderOverview/components/CreditCardOverview';
    import BoletoOverview from '@/components/OrderOverview/components/BoletoOverview';
    import PixOverview from '@/components/OrderOverview/components/PixOverview';
    import PixOpenFinanceOverview from '@/components/OrderOverview/components/PixOpenFinanceOverview';
    import ShippingMethodOverview from '@/components/OrderOverview/components/ShippingMethodOverview';
    import QrCodePayOverview from '@/components/OrderOverview/components/QrCodePayOverview';
    import ClickToPayOverview from '@/components/OrderOverview/components/ClickToPayOverview';
    import GooglePayOverview from '@/components/OrderOverview/components/GooglePayOverview';
    import ApplePayOverview from '@/components/OrderOverview/components/ApplePayOverview';
    import CartItems from '@/components/CartItems';
    import CartItemDescription from '@/components/CartItemDescription';
    import PaymentDateOverview from '@/components/OrderOverview/components/PaymentDateOverview';
    import MerchantIdentity from '@/components/MerchantIdentity';
    import TotalizerOverview from '@/components/OrderOverview/TotalizerOverview';

    export default {
        data() {
            return {
                shouldShowContent: false,
                firstItemDescription: '',
            };
        },

        props: {
            isMobile: {
                type: Boolean,
                default: false
            },
        },

        created() {
            this.firstItemDescription = this.getFirstItemDescription;
        },

        mounted() {
            if(this.atFirstStepOpenLinkSummary || !this.isMobile) {
                this.shouldShowContent = true;
                this.$store.commit('pipeline/setAtFirstStepOpenLinkSummary', false);
            }
        },

        computed: {
            ...mapGetters({
                formattedPrice: 'payment/getFormattedPrice',
                formattedSubtotalPrice: 'payment/getFormattedSubtotalPrice',
                selectedPaymentMethod: 'payment/getSelectedPaymentMethod',
                shippingMethod: 'shippingType/getMethod',
                showShippingMethod: 'shippingType/getShowShippingMethod',
                installments: 'payment/getInstallmentNumberOfPayments',
                hasConvenienceFee: 'convenienceFee/getHasConvenienceFee',
                atFirstStepOpenLinkSummary: 'pipeline/getAtFirstStepOpenLinkSummary',
                isDccAccepted: 'dcc/getIsAccepted',
                dccQuote: 'dcc/getQuote',
                dccCurrency: 'dcc/getCurrencyPresentation',
            }),

            collapsedTotalText: (self) => self.isDccAccepted
                ? `${self.dccCurrency.symbol} ${moneyHelper.formatMinorUnits(self.dccQuote.convertedAmount, self.dccCurrency.exponent)}`
                : `R$ ${self.formattedPrice}`,

            ...mapState({
                isCustomizationEnabled: state => state.merchantLayout.IsCustomizationEnabled,
                merchantName: state => state.preorder.MerchantName,
                items: state => state.cart.Items,
            }),

            getFirstItemDescription() {
                const { Description } = this.items.find((item) => item.Description) || {};
                
                return Description;
            },

            showPaymentInfo() {
            if(this.$store.state.payment.PaymentType === 100) {
                return this.selectedPaymentMethod && !!this.$store.state.clickToPayPayment.checkoutResponse.cardArtUri;
            }

            if(this.$store.state.payment.PaymentType === 101) {
                return this.selectedPaymentMethod && !!this.$store.state.googlePayPayment.selectedCard.description;
            }
            
            if(this.$store.state.payment.PaymentType === 102) {
                return !!this.installments;
            }

            return this.selectedPaymentMethod;
            },

            showPaymentDate() {
                if(this.$store.state.payment.PaymentType === 9) {
                    return this.selectedPaymentMethod && this.$store.state.pixOpenFinancePayment.paymentDate;
                }

                return false;
            }
        },

        components: {
            CreditCardOverview,
            ShippingMethodOverview,
            BoletoOverview,
            PixOverview,
            QrCodePayOverview,
            ClickToPayOverview,
            GooglePayOverview,
            ApplePayOverview,
            CartItems,
            CartItemDescription,
            PixOpenFinanceOverview,
            PaymentDateOverview,
            MerchantIdentity,
            TotalizerOverview
        }
    };
</script>

<style lang="scss" scoped>
    .link-summary {
        background: #EFF1F3;
        padding: 16px;

        @media screen and (min-width: 1025px) {
            border-radius: 20px;
            padding: 24px;
        }

        &__container {
            display: flex;
            flex-direction: column;
        }

        &__row {
            display: flex;
            align-items: center;
            gap: 16px;
        }
        
        // CTA
        &__cta {
            display: flex;
            align-items: center;
            flex-direction: row;
            justify-content: space-between;

            span {
                font-size: 16px;
                font-weight: 700;
                color: #2F363E;
            }

            .link-summary__toggle-button {
                background: none;
                border: none;
                padding: 0;
    
                span {
                    color: #0667CD;
                }
    
                img {
                    &.active {
                        transform: rotate(180deg);
                    }
                }
            }
        }


        // Areas        
        &__merchant-info {
            display: flex;
            flex-direction: column;

            span {
                font-size: 16px;
                font-weight: 700;
                color: #2F363E;

                @media screen and (min-width: 1025px) {
                    font-size: 20px;
                }
            }

            small {
                font-size: 12px;
                font-weight: 500;
                color: #5C6A7B;

                @media screen and (min-width: 1025px) {
                    font-size: 14px;
                }
            }
        }

        &__cart-items {}

        &__cart-info {}

        &__shipping-info {}

        &__payment-info {}

        // Text
        &__title {
            color: #2F363E;
            display: block;
            font-size: 16px;
            font-weight: 700;
            margin-bottom: 8px;
        }

        &__total-text {
            font-size: 16px;
            color: #2F363E;
            font-weight: 700;

            @media screen and (min-width: 1025px) {
                font-size: 20px;
            }
        }

        &__total-price {
            font-size: 16px;
            font-weight: 700;
            color: #2F363E;

            @media screen and (min-width: 1025px) {
                font-size: 20px;
            }
        }

        &__divider {
            border-color: #B8C1CC;
            margin: 16px 0;
        }
    }

    .link-summary-dropdown {
    }

    // Transition component
    .fade-enter-active,
    .fade-leave-active {
        transition: opacity .3s;
    }

    .fade-enter,
    .fade-leave-to
    /* .fade-leave-active em versões anteriores a 2.1.8 */
    {
        opacity: 0;
    }
</style>
